
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import ManualPage  from '../../components/ManualPage'

export const metadata = {};

const layoutProps = {
  metadata
};
const MDXLayout = ({children}) => <ManualPage image="/img/manual.gif">{children}</ManualPage>
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1 {...{
      "id": "facilitators"
    }}>{`Facilitators`}</h1>
    <p>{`Facilitators lead cohorts of Hyperlink courses and clubs. They are participants, but are also guides, responsible for shaping the learning environment. If a course is a score and a cohort is a performance, the facilitator is the conductor.`}</p>
    <h2 {...{
      "id": "getting-started"
    }}>{`Getting Started`}</h2>
    <h3 {...{
      "id": "who-should-facilitate"
    }}>{`Who Should Facilitate`}</h3>
    <p>{`We believe that powerful learning is possible between peers, and that very often leadership and structure matters more than specific expertise.`}</p>
    <p>{`We want facilitation to be open to all — but keep in mind that it can be a lot of work! Before you apply, please be sure that you are:`}</p>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Prepared to commit fully to the role.`}</strong>{` You will be compensated for your time, but make sure you have both space in your schedule `}<em parentName="li">{`and`}</em>{` mental energy to give this work the full attention it requires.`}</li>
      <li parentName="ol"><strong parentName="li">{`Qualified to facilitate a particular course`}</strong>{`. We encourage courses to be designed such that any learner up for the challenge can facilitate a cohort. However, this depends a lot on the course, and some may require specific expertise to facilitate. If you're eager to facilitate but not sure if you're ready, consider starting with a club instead.`}</li>
    </ol>
    <h3 {...{
      "id": "the-perks-of-facilitating"
    }}>{`The Perks of Facilitating`}</h3>
    <p>{`Facilitating has intrinsic benefits — for one, it can be a lot of fun; for two, it's a rewarding challenge — but comes with some concrete perks as well:`}</p>
    <ul>
      <li parentName="ul"><strong parentName="li">{`You will be compensated:`}</strong>{` Facilitators should be paid fairly for their efforts. Hyperlink takes 20% of tuition (same for courses and clubs), including payment processing fees. The other 80% goes straight to the facilitator.`}</li>
      <li parentName="ul"><strong parentName="li">{`You will learn `}<em parentName="strong">{`a lot:`}</em></strong>{` Facilitating a cohort (or several!) is a great way to explore a topic more deeply in a structured way, and shape the learning environment in service of particular learning goals.`}</li>
    </ul>
    <h3 {...{
      "id": "becoming-a-facilitator"
    }}>{`Becoming a Facilitator`}</h3>
    <ol>
      <li parentName="ol">{`If you're interested in facilitating an existing course, reach out to the course maintainer by direct message on the forum (`}<a parentName="li" {...{
          "href": "mailto:contact@hyperlink.academy"
        }}>{`email us`}</a>{` if you're not sure who to talk to). Be sure to mention:`}
        <ul parentName="li">
          <li parentName="ul">{`The date range(s) in which you're available to facilitate`}</li>
          <li parentName="ul">{`Any relevant qualifications, if needed`}</li>
        </ul>
      </li>
      <li parentName="ol">{`For now, we'll need to add you to the course as a `}<strong parentName="li"><em parentName="strong">{`maintainer`}</em></strong>{`. One of the most important powers of a maintainer is the ability to create and facilitate new cohorts.`}
        <ul parentName="li">
          <li parentName="ul">{`Eventually we may split out these powers so facilitating and maintaining are separated. For the time being, there's considerable overlap: different conceptual responsibilities, but same technical permissions on the site.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`There are a couple details we'll need to take care of in order to pay you properly. Keep an eye out for an email with info on the following:`}
        <ul parentName="li">
          <li parentName="ul">{`We use Stripe to handle both payments from learners, and payouts to facilitators. You'll need to connect a Stripe account from your `}<a parentName="li" {...{
              "href": "/dashboard?tab=Profile"
            }}>{`Profile`}</a>{` to receive these payouts.`}</li>
          <li parentName="ul">{`Hyperlink is technically hiring you as a contractor when you become a facilitator, so you'll also need to fill out a 1099 for tax purposes.`}</li>
        </ul>
      </li>
    </ol>
    <p><strong parentName="p">{`Once these steps are complete, you'll be ready to start facilitating!`}</strong></p>
    <h2 {...{
      "id": "facilitating-cohorts"
    }}>{`Facilitating Cohorts`}</h2>
    <h3 {...{
      "id": "creating-a-new-cohort"
    }}>{`Creating a New Cohort`}</h3>
    <ol>
      <li parentName="ol">{`Once you're a maintainer, you'll be able to edit course settings on the course page. From here, you can schedule and assign yourself to a new cohort.`}</li>
      <li parentName="ol">{`When you first create the cohort, it will be in draft mode — not yet live and visible to the public. This gives you time to set up your cohort before publishing it to the world.`}</li>
      <li parentName="ol">{`Navigate to your newly minted cohort page from the course page (or your homepage dashboard). There, you'll find a link to your cohort's private forum, which comes with some pre-populated posts you'll need to edit:`}
        <ul parentName="li">
          <li parentName="ul"><strong parentName="li">{`The "Notes" topic`}</strong>{`: add an overview of the cohort's schedule, and any changes to the curriculum specific to this cohort.`}</li>
          <li parentName="ul"><strong parentName="li">{`The "Getting Started" topic`}</strong>{`: let learners know how to prepare, with introductions and any logistical details.`}</li>
          <li parentName="ul"><strong parentName="li">{`Anything Else`}</strong>{`: other topics you'd like to create to get conversation going? Go ahead and create them on the forum.`}</li>
        </ul>
      </li>
      <li parentName="ol">{`Once you're all set up, go back to the cohort page and click the "Go Live" button.`}</li>
    </ol>
    <p>{`Note: for a Club, the steps are very similar but the workflow is slightly more streamlined. You can skip the "Notes" topic, for instance.`}</p>
    <p>{`Refer to the checklist at the top of your course / cohort page for details on the required steps as you go.`}</p>
    <p><strong parentName="p">{`Congrats, you've just created a cohort — get ready to facilitate!`}</strong></p>
    <h3 {...{
      "id": "facilitating-a-cohort"
    }}>{`Facilitating a Cohort`}</h3>
    <p>{`The specific shape of a facilitator's job is decided by the course curriculum. Below, we've outlined some general things you'll likely want to do as part of this process.`}</p>
    <ol>
      <li parentName="ol"><strong parentName="li">{`Before the cohort`}</strong>
        <ul parentName="li">
          <li parentName="ul">{`Familiarize yourself with the course curriculum.`}</li>
          <li parentName="ul">{`Set up your tools and resources: link to readings, schedule video calls, and set up any shared tools. Be sure to test before the cohort starts.`}</li>
          <li parentName="ul">{`Chat with learners: as the start date approaches, participants will slowly start joining the forum. Say hi, answer any questions, and get to know everyone!`}</li>
        </ul>
      </li>
      <li parentName="ol"><strong parentName="li">{`During the cohort`}</strong>
        <ul parentName="li">
          <li parentName="ul">{`Keep things running smoothly, following the course curriculum. Common things you may be responsible for include scheduling video meetings, distributing reading materials or assignments, and facilitating conversations.`}</li>
          <li parentName="ul">{`Put out fires if / when they happen. If something unexpected arises, be flexible and creative, and do your best to figure it out. We're here for you if you need help.`}</li>
          <li parentName="ul">{`Lean on the Hyperlink community for support! As a facilitator you'll have access to the Meta Course forum, where we can talk through any questions or issues regarding maintenance and facilitation.`}</li>
        </ul>
      </li>
      <li parentName="ol"><strong parentName="li">{`After the cohort`}</strong>
        <ul parentName="li">
          <li parentName="ul">{`Gather feedback and surface learning output, as a resource for the course. Two final discussion topics to make—`}
            <ul parentName="li">
              <li parentName="ul"><strong parentName="li">{`Artifacts:`}</strong>{` Every course on Hyperlink should result in learners producing some sort of artifact, whether an essay, final project, or research plan. Once the cohort is over, create a topic in the public course forum highlighting the artifact(s) that were produced.`}</li>
              <li parentName="ul"><strong parentName="li">{`Retrospective:`}</strong>{` Gather feedback from the learners and contribute your experience as a facilitator back to the course maintainers. What went well? What could be improved? We suggest starting a private discussion in the cohort forum, then summarizing the feedback in a topic on the public course forum.`}</li>
            </ul>
          </li>
          <li parentName="ul">{`Finally, mark your course as complete on the cohort page. The forum will always be open to you and members of the cohort, so feel free to stay and chat.`}</li>
          <li parentName="ul">{`Get paid! Assuming all the necessary documentation is complete, you should see a deposit come in soon after the course completes.`}</li>
        </ul>
      </li>
    </ol>
    <p>{`Again, if you're facilitating a Club, most of your responsibilities will be the same. But you'll typically be following an outline more than a detailed curriculum, co-creating the learning experience with other participants.`}</p>
    </MDXLayout>;
}

;
MDXContent.isMDXComponent = true;